import { Component } from '@theme/component';

/**
 * @typedef {Object} SDFlipCardRefs
 * @property {HTMLButtonElement} [toggle] - Toggle button element
 */

/**
 * SD Flip Card - Expandable card with image/video background
 * @extends {Component<SDFlipCardRefs>}
 */
class SDFlipCard extends Component {
  /** @type {function} */
  #boundHandleKeydown = this.#handleKeydown.bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#boundHandleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#boundHandleKeydown);
  }

  /**
   * Handles toggle button click (called via on:click attribute)
   * @param {Event} event - Click event
   */
  handleToggle(event) {
    event.stopPropagation();

    const isPressed = this.dataset.pressed === 'true';
    const newState = !isPressed;

    this.dataset.pressed = newState;

    if (this.refs.toggle) {
      this.refs.toggle.setAttribute('aria-pressed', newState);
    }

    // Only scroll into view when expanding AND card is not fully visible
    if (newState) {
      const rect = this.getBoundingClientRect();
      const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isFullyVisible) {
        const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: motionOk ? 'smooth' : 'instant', block: 'center' });
      }
    }
  }

  /**
   * Handles Escape key to collapse expanded card
   * Only responds when focus is within this card instance
   * @param {KeyboardEvent} event - Keyboard event
   */
  #handleKeydown(event) {
    if (
      event.key === 'Escape' &&
      this.dataset.pressed === 'true' &&
      this.contains(document.activeElement)
    ) {
      this.dataset.pressed = 'false';

      if (this.refs.toggle) {
        this.refs.toggle.setAttribute('aria-pressed', 'false');
        this.refs.toggle.focus();
      }
    }
  }
}

if (!customElements.get('sd-flip-card')) {
  customElements.define('sd-flip-card', SDFlipCard);
}
